import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import QuoteRequestModal from "../../../components/Store/QuoteRequestModal"
const API = process.env.REACT_APP_API_URL || "";

const ProductDetailsPage = () => {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);
    const [isQuoteOpen, setIsQuoteOpen] = useState(false);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const { data } = await axios.get(`${API}/api/store/products/${slug}`);
                setProduct(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [slug]);

    if (loading) {
        return <div className="min-h-screen bg-neutral-950 flex items-center justify-center"><div className="animate-pulse w-16 h-16 bg-purple-600/50 rounded-full blur-xl" /></div>;
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-neutral-950 text-white flex flex-col items-center justify-center">
                <h1 className="text-4xl font-bold mb-4">Product Not Found</h1>
                <Link to="/store/products" className="text-purple-400 hover:underline">Return to Store</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-950 text-white font-sans pt-32 pb-24 px-6 md:px-12">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* Image Gallery */}
                    <div className="space-y-6">
                        <div className="aspect-square bg-neutral-900/30 rounded-3xl border border-neutral-800 p-8 flex items-center justify-center relative overflow-hidden">
                            <AnimatePresence mode="wait">
                                {product.images?.[activeImage] ? (
                                    <motion.img
                                        key={activeImage}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 1.05 }}
                                        transition={{ duration: 0.3 }}
                                        src={product.images[activeImage].imageUrl}
                                        alt={product.name}
                                        className="max-w-full max-h-full object-contain"
                                    />
                                ) : (
                                    <span className="text-neutral-600">No Image Available</span>
                                )}
                            </AnimatePresence>
                        </div>
                        {product.images && product.images.length > 1 && (
                            <div className="flex gap-4 overflow-x-auto pb-2">
                                {product.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImage(idx)}
                                        className={`shrink-0 w-20 h-20 rounded-xl border-2 overflow-hidden ${activeImage === idx ? 'border-purple-500' : 'border-neutral-800 hover:border-neutral-600'}`}
                                    >
                                        <img src={img.imageUrl} alt="thumbnail" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="flex flex-col">
                        <div className="mb-8 border-b border-neutral-800 pb-8">
                            <div className="text-sm font-bold text-purple-400 tracking-widest uppercase mb-4">
                                {product.category?.name || 'Hardware'}
                            </div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">{product.name}</h1>
                            <div className="flex items-center gap-4 mb-6">
                                <span className="font-mono text-3xl">
                                    {product.discountPrice ? (
                                        <div className="flex items-center gap-4">
                                            <span className="text-white">₹{product.discountPrice}</span>
                                            <span className="text-lg text-neutral-500 line-through">₹{product.basePrice}</span>
                                        </div>
                                    ) : (
                                        <span>₹{product.basePrice}</span>
                                    )}
                                </span>
                            </div>
                            <p className="text-neutral-300 text-lg leading-relaxed">
                                {product.shortDescription}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="mb-12">
                            {product.status === 'out_of_stock' ? (
                                <button disabled className="w-full bg-neutral-800 text-neutral-500 font-bold py-5 rounded-full cursor-not-allowed">
                                    OUT OF STOCK
                                </button>
                            ) : (
                                <button
                                    onClick={() => setIsQuoteOpen(true)}
                                    className="w-full bg-white text-black font-bold py-5 rounded-full hover:bg-purple-100 hover:scale-[1.02] transition-all"
                                >
                                    REQUEST A QUOTE
                                </button>
                            )}
                        </div>

                        {/* Specifications Accordion (Rich Text) */}
                        {product.richDescription && (
                            <div className="prose prose-invert max-w-none">
                                <h3 className="text-xl font-bold mb-4">Product Details</h3>
                                <div dangerouslySetInnerHTML={{ __html: product.richDescription }} className="text-neutral-400" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <QuoteRequestModal
                product={product}
                isOpen={isQuoteOpen}
                onClose={() => setIsQuoteOpen(false)}
            />
        </div>
    );
};

export default ProductDetailsPage;